import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiAward, FiUsers, FiHeart, FiTarget } = FiIcons;

const About = () => {
  const stats = [
    { number: '25+', label: 'Years Experience' },
    { number: '10,000+', label: 'Happy Patients' },
    { number: '15+', label: 'Expert Dentists' },
    { number: '99%', label: 'Success Rate' }
  ];

  const team = [
    {
      name: 'Dr. Sarah Johnson',
      role: 'Lead Dentist & Founder',
      image: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=300&h=300&fit=crop',
      experience: '15+ years experience in general and cosmetic dentistry'
    },
    {
      name: 'Dr. Michael Chen',
      role: 'Orthodontist',
      image: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=300&h=300&fit=crop',
      experience: 'Specialist in orthodontics and dental alignment'
    },
    {
      name: 'Dr. Emily Davis',
      role: 'Pediatric Dentist',
      image: 'https://images.unsplash.com/photo-1594824475317-0d6b3a5f9a7e?w=300&h=300&fit=crop',
      experience: 'Expert in children\'s dental care and oral health'
    }
  ];

  const values = [
    {
      icon: FiHeart,
      title: 'Compassionate Care',
      description: 'We treat every patient with kindness, understanding, and respect.'
    },
    {
      icon: FiAward,
      title: 'Excellence',
      description: 'We strive for the highest standards in everything we do.'
    },
    {
      icon: FiUsers,
      title: 'Patient-Centered',
      description: 'Your comfort and satisfaction are our top priorities.'
    },
    {
      icon: FiTarget,
      title: 'Innovation',
      description: 'We use the latest technology and techniques for optimal results.'
    }
  ];

  return (
    <div>
      <Helmet>
        <title>About Us - DentalCare | 25+ Years of Expert Dental Care</title>
        <meta name="description" content="Learn about DentalCare's 25+ year history of providing exceptional dental services. Meet our expert team of dentists and discover our commitment to patient-centered care." />
        <meta name="keywords" content="about dentalcare, dental team, experienced dentists, dental clinic history, patient care, dental professionals" />
        <link rel="canonical" href="https://dentalcare.com/about" />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-900 to-emerald-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-5xl font-bold mb-6">About DentalCare</h1>
            <p className="text-xl text-emerald-100 max-w-3xl mx-auto">
              For over 25 years, we've been dedicated to providing exceptional dental care 
              with a personal touch. Our mission is to help you achieve optimal oral health 
              while ensuring your comfort every step of the way.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-emerald-600 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl font-bold text-emerald-900 mb-6">
                Our Story
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                DentalCare was founded in 1998 with a simple vision: to provide 
                world-class dental care in a warm, welcoming environment. What started 
                as a small practice has grown into a comprehensive dental center, but 
                our commitment to personalized care remains unchanged.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                We believe that every smile tells a story, and we're honored to be 
                part of yours. Our team combines years of experience with the latest 
                dental technology to ensure you receive the best possible care.
              </p>
              <p className="text-lg text-gray-700">
                From routine cleanings to complex procedures, we approach every 
                treatment with the same dedication to excellence and patient comfort 
                that has made us a trusted name in dental care.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <img
                src="https://images.unsplash.com/photo-1551601651-2a8555f1a136?w=600&h=500&fit=crop"
                alt="DentalCare Clinic Interior - Modern dental office"
                className="rounded-lg shadow-lg w-full h-96 object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-emerald-900 mb-4">
              Our Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These core values guide everything we do and shape the way we 
              interact with our patients and community.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center p-6"
              >
                <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={value.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-emerald-900 mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-emerald-900 mb-4">
              Meet Our Team
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our experienced team of dental professionals is dedicated to 
              providing you with the highest quality care in a comfortable environment.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-lg overflow-hidden text-center"
              >
                <img
                  src={member.image}
                  alt={`${member.name} - ${member.role} at DentalCare`}
                  className="w-full h-64 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-emerald-900 mb-2">
                    {member.name}
                  </h3>
                  <p className="text-emerald-600 font-medium mb-3">{member.role}</p>
                  <p className="text-gray-600">{member.experience}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;